import axios, { InternalAxiosRequestConfig } from "axios";
import { authTokenService } from "../utilities/authTokenService";
import { AESCryptoService } from "../utilities/cryptoService";
import { isEnvCdnUrl } from "../utilities/cdnUtils";
import { handleError } from "../utilities/errors";

const BASE_URL = process.env.REACT_APP_API_ROOT;
const USE_PROXY = process.env.REACT_APP_USE_PROXY === "true";

// Extended Axios request config
export interface ExtendedAxiosRequestConfig extends InternalAxiosRequestConfig {
  skipAuth?: boolean;
  noResponseParse?: boolean;
  onTokenExpired?: () => void;
  _retry?: boolean; // Internal flag for retry logic
}

// Logout handler - called when authentication fails
let logoutHandler: ((expired: boolean) => void) | null = null;
const setLogoutHandler = (handler: ((expired: boolean) => void) | null) => {
  logoutHandler = handler;
};

// Refresh token handler - called to attempt token refresh
let refreshTokenHandler: (() => Promise<boolean>) | null = null;
const setRefreshTokenHandler = (handler: (() => Promise<boolean>) | null) => {
  refreshTokenHandler = handler;
};
// Token expired callback - called when token expires (from FetchOptions)
let onTokenExpiredCallback: (() => void) | null = null;
const setOnTokenExpiredCallback = (callback: (() => void) | null) => {
  onTokenExpiredCallback = callback;
};

// Flag to prevent multiple simultaneous redirects
let isRedirecting = false;
const handleAuthRedirect = () => {
  if (isRedirecting) return; // Prevent multiple redirects
  isRedirecting = true;
  // Get all current URL parameters
  const currentParams = new URLSearchParams(window.location.search);
  const loginParams = new URLSearchParams();
  // Copy all existing parameters
  currentParams.forEach((value, key) => {
    loginParams.append(key, value);
  });
  // Add 'redirect' parameter with current path if not already present
  if (!loginParams.has("redirect")) {
    loginParams.append("redirect", window.location.pathname);
  }
  // Construct the login URL with all parameters
  const loginUrl = `/login${loginParams.toString() ? `?${loginParams.toString()}` : ""}`;
  // Redirect to login
  if (window.location?.pathname !== "/login") {
    window.location.href = loginUrl;
  }
  isRedirecting = false;
};
// Reset redirect flag (useful for testing or after successful login)
const resetRedirectFlag = () => {
  isRedirecting = false;
};

const getApiRoot = () => {
  // When using proxy, return the proxy path
  //  For non-proxy mode (local development), use the configured API root
  return USE_PROXY ? BASE_URL || "/api" : BASE_URL || "https://local.api.culturemap.app:8443/";
};

const REACT_APP_API_ROOT = getApiRoot();

const getOAuthRoot = () => {
  if (process.env.REACT_APP_USE_PROXY === "true") {
    return "/o"; // Use the proxy path
  }
  // For non-proxy mode, use the API root
  return `${REACT_APP_API_ROOT}o`;
};

//@ Todo proxy logic can be optimized on API implementation
const getFinalUrl = (url: string) => {
  if (!url) return "";
  let finalUrl = url;
  // First, clean up the input URL by removing any leading /api/
  finalUrl = finalUrl.replace(/^\/?(api\/)+/, "");
  if (USE_PROXY) {
    // In proxy mode, always prefix with single /api/
    finalUrl = `/api/${finalUrl}`;
  } else {
    // For non-proxy mode
    const cleanRoot = REACT_APP_API_ROOT.replace(/\/+$/, "");
    finalUrl = `${cleanRoot}/api/${finalUrl}`;
  }
  return finalUrl;
};

const api = axios.create({
  baseURL: REACT_APP_API_ROOT,
  timeout: 10000,
  headers: {
    Accept: "application/json"
  },
  withCredentials: true
});

const PUBLIC_ROUTES = ["o/token/"];

// REQUEST INTERCEPTOR
api.interceptors.request.use(
  (config: ExtendedAxiosRequestConfig) => {
    // Check if redirect is in progress
    if (isRedirecting) {
      return Promise.reject(handleError(new Error("Authentication redirect in progress")));
    }
    // Skip public routes
    if (PUBLIC_ROUTES.some((route) => config.url?.includes(route))) {
      return config;
    }
    // Skip auth if explicitly requested
    if (config.skipAuth) {
      return config;
    }
    // Handle URL construction for non-absolute URLs
    if (!config.url?.startsWith("http")) {
      const finalUrl = getFinalUrl(config.url || "");
      config.baseURL = "";
      config.url = finalUrl;
    }
    // Validate URL doesn't contain undefined
    if (config.url?.includes("undefined")) {
      return Promise.reject(handleError(new Error("Malformed URL: URL contains undefined")));
    }
    // Add authorization header
    const { accessToken } = authTokenService();
    if (accessToken && !config.headers.Authorization) {
      config.headers.Authorization = `Bearer ${accessToken}`;
    }
    // Handle FormData - remove Content-Type to let browser set it with boundary
    if (config.data instanceof FormData) {
      delete config.headers["Content-Type"];
    }
    return config;
  },
  (error) => {
    return Promise.reject(handleError(error));
  }
);

// RESPONSE INTERCEPTOR
api.interceptors.response.use(
  (response) => {
    // For 204 No Content responses, return response as-is
    if (response.status === 204) {
      return response;
    }
    return response;
  },
  async (error) => {
    const originalRequest = error.config as ExtendedAxiosRequestConfig;

    // Prevent infinite retry loops
    if (!originalRequest || originalRequest._retry) {
      return Promise.reject(error);
    }

    const statusCode = error?.response?.status;

    // Handle 401 Unauthorized
    if (statusCode === 401) {
      const isTokenNotValid = error.response?.data?.code === "token_not_valid";

      // Try refresh token first if handler is registered
      if (refreshTokenHandler && !originalRequest._retry) {
        originalRequest._retry = true;

        try {
          const refreshed = await refreshTokenHandler();
          if (refreshed) {
            // Get new token and retry
            const { accessToken } = authTokenService();
            if (accessToken) {
              originalRequest.headers.Authorization = `Bearer ${accessToken}`;
              return api(originalRequest);
            }
          }
        } catch (refreshError) {
          console.error("Token refresh failed:", refreshError);
        }
      }

      // Try internal refresh if no external handler
      if (isTokenNotValid && !originalRequest._retry) {
        originalRequest._retry = true;
        const { refreshToken } = authTokenService();

        if (refreshToken) {
          try {
            const response = await api.post("/o/token/refresh/", {
              refresh: refreshToken
            });

            // Update stored token
            const cryptoService = new AESCryptoService();
            const encryptedAccessToken = cryptoService.encrypt(response.data.access_token);
            localStorage.setItem("accessToken", encryptedAccessToken?.toString());

            // Update headers and retry
            api.defaults.headers.Authorization = `Bearer ${response.data.access_token}`;
            originalRequest.headers.Authorization = `Bearer ${response.data.access_token}`;

            return api(originalRequest);
          } catch (refreshError) {
            // Refresh failed, proceed to logout
            console.error("Token refresh failed:", refreshError);
          }
        }
      }

      // Call token expired callback if registered
      if (originalRequest.onTokenExpired) {
        originalRequest.onTokenExpired();
      }
      if (onTokenExpiredCallback) {
        onTokenExpiredCallback();
      }

      // Call logout handler if registered
      if (logoutHandler) {
        logoutHandler(true);
      } else {
        localStorage.removeItem("accessToken");
      }

      // Redirect to login
      handleAuthRedirect();

      return Promise.reject(handleError(new Error("Authentication expired")));
    }

    // Handle 403 Forbidden (refresh token expired) and Cloudfront authorization issue
    if (statusCode === 403) {
      const isPermissionDenied = error.response?.data?.code === "permission_denied";

      if (isPermissionDenied) {
        if (logoutHandler) {
          logoutHandler(true);
        } else {
          localStorage.clear();
        }
        handleAuthRedirect();
      }
    }

    // Handle cloud-front unauthorized and forbidden request
    if (statusCode === 401 || statusCode === 403) {
      // check whether current request url is on our CDN (the backend's mediaHost once config loads)
      const isCloudfrontReqUrl = isEnvCdnUrl(error?.config?.url);
      if (isCloudfrontReqUrl) {
        if (logoutHandler) {
          logoutHandler?.(true);
        }
      }
    }

    // reset redirect flag once all redirecting complete
    resetRedirectFlag();

    return Promise.reject(handleError(error));
  }
);

// RETRY FUNCTIONALITY
interface RetryConfig {
  maxRetries?: number;
  retryDelay?: number;
  retryCondition?: (error: any) => boolean;
}
const defaultRetryCondition = (error: any): boolean => {
  // Retry on 502 Bad Gateway errors
  return error.response?.status === 502 || error.message?.includes("502") || error.code === "ECONNABORTED"; // Timeout
};
const apiWithRetry = async <T = any>(config: Parameters<typeof api.request>[0] & RetryConfig): Promise<T> => {
  const { maxRetries = 3, retryDelay = 1000, retryCondition = defaultRetryCondition, ...axiosConfig } = config;
  for (let attempt = 1; attempt <= maxRetries; attempt++) {
    try {
      const response = await api.request<T>(axiosConfig);
      return response.data;
    } catch (error: any) {
      if (retryCondition(error)) {
        console.warn(`Attempt ${attempt}: Got error, retrying...`, error.message);

        if (attempt === maxRetries) {
          throw new Error(`Failed after ${maxRetries} retries`);
        }

        // Exponential backoff
        await new Promise((resolve) => setTimeout(resolve, retryDelay * attempt));
        continue;
      }

      // For non-retryable errors, throw immediately
      throw error;
    }
  }

  throw new Error(`Failed after ${maxRetries} retries`);
};

const OAuthApi = axios.create({
  baseURL: getOAuthRoot(),
  withCredentials: true
});

// Normalized error handling for OAuth requests
OAuthApi.interceptors.response.use(
  (response) => response,
  (error) => Promise.reject(handleError(error))
);

const getOAuthTokenUrl = () => {
  if (USE_PROXY) {
    return "/o/token/";
  }
  return `${getOAuthRoot()}/token/`;
};

// OAuth token request helper
const fetchOAuthToken = async (formData: FormData) => {
  const tokenUrl = getOAuthTokenUrl();
  try {
    const response = await OAuthApi.post(tokenUrl, formData, {
      headers: {
        // Let axios set Content-Type for FormData
      }
    });
    return response.data;
  } catch (error) {
    throw error;
  }
};

// retry on failed can also be handled from tanstack-query
// https://tanstack.com/query/v5/docs/framework/react/guides/query-retries

export {
  api,
  OAuthApi,

  // Handler setters
  setLogoutHandler,
  setRefreshTokenHandler,
  setOnTokenExpiredCallback,
  resetRedirectFlag,

  // URL helpers
  REACT_APP_API_ROOT,
  getOAuthRoot,
  getOAuthTokenUrl,
  fetchOAuthToken,

  // Retry
  apiWithRetry
};
